/**
 * Rule version history + rollback panel (#119, REQ4.5).
 *
 * Embedded into the rule edit dialog as a separate "Versionen"-Tab. Loads
 * `GET /rules/:id/versions` (current + history snapshots) and lets the
 * admin restore a previous version via `POST /rules/:id/rollback`. The
 * rollback path piggy-backs on the standard update audit trail server-side
 * so this UI stays focused on selection + confirm.
 *
 * Pragmatic Sprint-2-closure: we do NOT render a side-by-side text-diff
 * (architect spec calls it optional). Instead each row shows the title +
 * a 200-char body excerpt so the operator can identify the version at a
 * glance. A full diff lands with #61 (notifications) when we have a
 * dedicated diff component.
 */
import type { RuleVersionListItem } from '@invoice/shared/schemas';
import { CheckCircle2, History, Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useRollbackRule, useRuleVersions } from '@/lib/queries/rules';
import { cn } from '@/lib/utils';

interface RuleVersionsPanelProps {
  ruleId: string;
  /** Notify the host (the edit dialog) once a rollback succeeds so it can refresh. */
  onRolledBack?: (newVersion: number) => void;
}

export function RuleVersionsPanel({ ruleId, onRolledBack }: RuleVersionsPanelProps) {
  const versionsQuery = useRuleVersions(ruleId);
  const rollback = useRollbackRule();
  const [confirmTarget, setConfirmTarget] = useState<number | null>(null);

  const handleRollback = async (toVersion: number) => {
    setConfirmTarget(null);
    const result = await rollback.mutateAsync({
      id: ruleId,
      body: { toVersion },
    });
    onRolledBack?.(result.newVersion);
  };

  if (versionsQuery.isPending) {
    return (
      <div
        data-testid="rule-versions-loading"
        className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground"
      >
        <Loader2 className="size-4 animate-spin" aria-hidden />
        Versionen werden geladen…
      </div>
    );
  }

  if (versionsQuery.isError) {
    return (
      <div
        role="alert"
        data-testid="rule-versions-error"
        className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
      >
        Versionen konnten nicht geladen werden: {versionsQuery.error.message}
      </div>
    );
  }

  const { current, history } = versionsQuery.data;

  return (
    <div className="flex flex-col gap-3" data-testid="rule-versions-panel">
      <header className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <History className="size-4" aria-hidden />
          Versionen ({history.length} historisch)
        </div>
      </header>

      <ul className="flex flex-col gap-2">
        <li>
          <VersionRow
            entry={current}
            isCurrent
            disableActions={rollback.isPending}
            onRollback={() => undefined}
          />
        </li>
        {history.map((entry) => (
          <li key={entry.id}>
            <VersionRow
              entry={entry}
              isCurrent={false}
              disableActions={rollback.isPending}
              onRollback={() => setConfirmTarget(entry.version)}
            />
          </li>
        ))}
        {history.length === 0 ? (
          <li
            data-testid="rule-versions-empty"
            className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-4 text-center text-xs text-muted-foreground"
          >
            Noch keine historischen Versionen. Änderungen erscheinen hier nach dem ersten
            Speichern.
          </li>
        ) : null}
      </ul>

      {rollback.isError ? (
        <div
          role="alert"
          data-testid="rule-versions-rollback-error"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
        >
          Rollback fehlgeschlagen: {rollback.error.message}
        </div>
      ) : null}

      {confirmTarget !== null ? (
        <ConfirmRollback
          version={confirmTarget}
          inFlight={rollback.isPending}
          onCancel={() => setConfirmTarget(null)}
          onConfirm={() => {
            void handleRollback(confirmTarget);
          }}
        />
      ) : null}
    </div>
  );
}

interface VersionRowProps {
  entry: RuleVersionListItem;
  isCurrent: boolean;
  disableActions: boolean;
  onRollback: () => void;
}

function VersionRow({ entry, isCurrent, disableActions, onRollback }: VersionRowProps) {
  const editedAt = formatDateTime(entry.editedAt);
  return (
    <div
      data-testid={`rule-version-row-${entry.version}`}
      className={cn(
        'flex flex-col gap-2 rounded-md border border-border bg-card px-3 py-2',
        isCurrent && 'border-accent/40 bg-accent/5',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          v{entry.version}
          {isCurrent ? (
            <Badge variant="success" className="text-[10px] uppercase">
              <CheckCircle2 className="size-3" aria-hidden />
              Aktiv
            </Badge>
          ) : null}
          <span className="text-xs font-normal text-muted-foreground">{entry.title}</span>
        </div>
        {!isCurrent ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disableActions}
            onClick={onRollback}
            aria-label={`Auf Version ${entry.version} zuruecksetzen`}
            data-testid={`rule-version-restore-${entry.version}`}
          >
            <RotateCcw className="size-3.5" aria-hidden />
            Wiederherstellen
          </Button>
        ) : null}
      </div>
      <p className="text-[11px] text-muted-foreground">
        {editedAt} · von {entry.editorEmail}
      </p>
      <p className="line-clamp-3 whitespace-pre-wrap font-mono text-[11px] text-muted-foreground">
        {entry.body.slice(0, 240)}
        {entry.body.length > 240 ? '…' : ''}
      </p>
    </div>
  );
}

interface ConfirmRollbackProps {
  version: number;
  inFlight: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmRollback({ version, inFlight, onCancel, onConfirm }: ConfirmRollbackProps) {
  return (
    <div
      role="alertdialog"
      aria-labelledby="rule-rollback-confirm-title"
      data-testid="rule-rollback-confirm"
      className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
    >
      <p id="rule-rollback-confirm-title">
        Version {version} wirklich wiederherstellen? Die aktuelle Fassung wird als historischer
        Snapshot weiter geschrieben.
      </p>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={inFlight}>
          Abbrechen
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onConfirm}
          disabled={inFlight}
          data-testid="rule-rollback-confirm-ok"
        >
          {inFlight ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Wiederherstellen
        </Button>
      </div>
    </div>
  );
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}
